/**
 * 재귀적 재시도 유틸
 *
 * 실패 시 delayMs만큼 대기 후 재시도하며, remaining이 0이 되면 최종 에러를 throw
 * shouldRetry로 재시도 여부를 판단 (타입 검증 에러 등은 재시도해도 무의미)
 *
 * @param fn - 실행할 비동기 함수
 * @param options.maxRetries - 최대 재시도 횟수 (기본 2)
 * @param options.delayMs - 재시도 간격 ms (기본 500)
 * @param options.shouldRetry - 에러를 받아 재시도 여부 반환. false면 즉시 throw (기본 항상 true)
 * @param options.formatError - 최종 실패 시 에러 메시지 포맷
 */

interface RetryOptions {
  maxRetries?: number
  delayMs?: number
  shouldRetry?: (error: unknown) => boolean
  formatError?: (error: unknown, totalAttempts: number) => string
}

const DEFAULT_MAX_RETRIES = 2
const DEFAULT_DELAY_MS = 500

export async function withRetry<T>(fn: () => Promise<T>, options?: RetryOptions): Promise<T> {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    delayMs = DEFAULT_DELAY_MS,
    shouldRetry = () => true,
    formatError = (error: unknown, totalAttempts: number) =>
      `Failed after ${totalAttempts} attempts: ${error instanceof Error ? error.message : String(error)}`,
  } = options ?? {}

  const attempt = async (remaining: number): Promise<T> => {
    try {
      return await fn()
    } catch (error) {
      // 재시도 불가능한 에러 → 즉시 throw
      if (!shouldRetry(error)) throw error

      // 재시도 횟수 소진 → 최종 에러
      if (remaining <= 0) {
        throw new Error(formatError(error, maxRetries + 1))
      }

      // 대기 후 재시도
      await new Promise((resolve) => setTimeout(resolve, delayMs))
      return attempt(remaining - 1)
    }
  }

  return attempt(maxRetries)
}
